import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash } from '@phosphor-icons/react';
import { useCart } from '../context/CartContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';

export const CartSheet = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    isCartOpen, 
    setIsCartOpen, 
    updateCartItem, 
    removeFromCart, 
    getCartTotal,
    loading 
  } = useCart();

  const handleCheckout = () => {
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="bg-black border-border w-full sm:max-w-lg flex flex-col" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="text-left text-2xl tracking-tighter uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            YOUR CART
          </SheetTitle>
        </SheetHeader>
        
        {!cart || !cart.items || cart.items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground text-lg mb-4">YOUR CART IS EMPTY</p>
              <Button 
                onClick={() => {
                  setIsCartOpen(false);
                  navigate('/shop/men');
                }}
                className="bg-white text-black hover:bg-primary hover:text-white transition-colors"
                data-testid="continue-shopping-btn"
              >
                CONTINUE SHOPPING
              </Button>
            </div>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 py-4">
                {cart.items.map((item, index) => (
                  <div key={`${item.product_id}-${item.size}`} className="border border-border p-4" data-testid={`cart-item-${index}`}>
                    <div className="flex gap-4">
                      <div className="w-20 h-24 bg-muted overflow-hidden">
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "'Oswald', sans-serif" }}>
                          {item.name}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1">SIZE: {item.size}</p>
                        <p className="text-sm font-bold mt-2">{formatPrice(item.price)}</p>
                        
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-border">
                            <button
                              onClick={() => updateCartItem(item.product_id, item.size, item.quantity - 1)}
                              disabled={loading}
                              className="p-2 hover:bg-muted transition-colors"
                              data-testid={`decrease-qty-${index}`}
                              aria-label="Decrease quantity"
                            >
                              <Minus size={14} />
                            </button>
                            <span className="px-4 text-sm font-bold" data-testid={`item-qty-${index}`}>
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateCartItem(item.product_id, item.size, item.quantity + 1)}
                              disabled={loading}
                              className="p-2 hover:bg-muted transition-colors"
                              data-testid={`increase-qty-${index}`}
                              aria-label="Increase quantity"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.product_id, item.size)}
                            disabled={loading}
                            className="p-2 text-muted-foreground hover:text-primary transition-colors"
                            data-testid={`remove-item-${index}`}
                            aria-label="Remove item"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-border pt-4 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm uppercase tracking-wide text-muted-foreground">SUBTOTAL</span>
                <span className="text-xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }} data-testid="cart-subtotal">
                  {formatPrice(getCartTotal())}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">SHIPPING CALCULATED AT CHECKOUT</p>
              <Button 
                onClick={handleCheckout}
                className="w-full bg-white text-black hover:bg-primary hover:text-white transition-colors h-12 text-base font-bold uppercase tracking-wide"
                data-testid="checkout-btn"
              >
                CHECKOUT
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
